import { type ColumnDef } from "components/table";
import { InfoIcon } from "lucide-react";
import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { IProduct } from "integrations/firebase/collections";
import { TFunction } from "i18next";
import { TableStatusBadges } from "components/GenerationStatusBadge/TableStatusBadges";
import { getStatusesFromActions } from "components/GenerationStatusBadge/utils";
import { Checkbox } from "components/ui/checkbox";
import { TruncatedText } from "components/TruncatedText";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Text } from "components/text";
import { FirestoreGenerationStatus } from "reedy-data/models";
import { type APlusBadgeStatus } from "../hooks/useAmplifyStatus";
import { APlusStatusBadge } from "./APlusStatusBadge";

const undefinedWhenEmpty = (value?: string | null) => {
  const trimmedValue = value?.trim();

  return trimmedValue ? trimmedValue.toLocaleLowerCase() : undefined;
};

const joinValues = (values?: string[] | null) =>
  undefinedWhenEmpty(values?.join(", "));

const publisherNames = (product: IProduct) => {
  const { publishers } = product;

  if (publishers?.length) {
    return undefinedWhenEmpty(
      publishers.map((publisher) => publisher.name).join(", "),
    );
  }

  return undefinedWhenEmpty(product.publisher);
};

const dateFromPublishedAt = (publishedAt: IProduct["publishedAt"]) => {
  let date: Date | null = null;

  if (publishedAt instanceof Date) {
    date = publishedAt;
  } else if (typeof publishedAt === "string") {
    date = new Date(publishedAt);
  } else if (publishedAt && "toDate" in publishedAt) {
    date = publishedAt.toDate();
  }

  if (!date || Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const sortableDate = (publishedAt: IProduct["publishedAt"]) =>
  dateFromPublishedAt(publishedAt)?.getTime();

const generationStatusRank: Record<FirestoreGenerationStatus, number> = {
  [FirestoreGenerationStatus.ERROR]: 0,
  [FirestoreGenerationStatus.REQUESTED]: 1,
  [FirestoreGenerationStatus.GENERATING]: 2,
  [FirestoreGenerationStatus.GENERATED]: 2,
  [FirestoreGenerationStatus.PROCESSING]: 2,
  [FirestoreGenerationStatus.COMPLETED]: 3,
  [FirestoreGenerationStatus.CANCELLED]: 4,
  [FirestoreGenerationStatus.IMPORTED]: 5,
};

const generationSortValue = (product: IProduct) => {
  const { keywordsStatus, subjectsStatus, descriptionStatus } =
    getStatusesFromActions(product.actions);

  return Math.min(
    generationStatusRank[keywordsStatus],
    generationStatusRank[subjectsStatus],
    generationStatusRank[descriptionStatus],
  );
};

const formatPublishedAt = (publishedAt: IProduct["publishedAt"]) => {
  const date = dateFromPublishedAt(publishedAt);

  return date?.toISOString().slice(0, 10) ?? null;
};

export const columns: (
  t: TFunction,
  options?: {
    isProductInactive?: (product: IProduct) => boolean;
    aPlusStatus?: (productId: string) => APlusBadgeStatus;
  },
) => ColumnDef<IProduct>[] = (t, options) => [
  {
    id: "select",
    ignoreColumnClick: true,
    size: 50,
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => {
          table.toggleAllPageRowsSelected(!!value);
          table.setLastSelectedRowId(null);
        }}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onClick={(event) => row.toggleSelectedWithRange(event.shiftKey)}
        aria-label={`Select row ${row.original.id}`}
        disabled={!row.getCanSelect()}
        className="translate-y-[2px] select-none"
      />
    ),
  },
  {
    id: "isbn",
    accessorFn: (product) => product.isbn ?? product.id,
    size: 120,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.isbn")}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center justify-between">
          <Text as="div" variant={row.original.nonExistent ? "error" : "body"}>
            {row.original.isbn || row.original.id}
          </Text>
          {options?.isProductInactive?.(row.original) && (
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon
                  className="h-5 w-5 text-red-500 ml-2 inline-block"
                  aria-label={t("projectDetails:table.inactiveStatusTooltip")}
                  data-testid="product-inactive-icon"
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <Text variant="body">
                  {t("projectDetails:table.inactiveStatusTooltip")}
                </Text>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    id: "author",
    accessorFn: (product) => joinValues(product.author),
    size: 180,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.author")}
      />
    ),
    cell: ({ row }) => {
      const authors = row.original.author;

      return (
        <div className="max-w-[500px] flex space-x-2">
          <TruncatedText text={authors?.join(", ")} />
        </div>
      );
    },
  },
  {
    id: "title",
    accessorFn: (product) => undefinedWhenEmpty(product.title),
    size: 300,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.title")}
      />
    ),
    cell: ({ row }) => (
      <div className="max-w-[500px] flex space-x-2">
        <TruncatedText text={row.original.title} />
      </div>
    ),
  },
  {
    id: "productGroupDescription",
    accessorFn: (product) =>
      undefinedWhenEmpty(product.productGroupDescription),
    size: 150,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.productForm")}
      />
    ),
    cell: ({ row }) => (
      <div className="flex items-center">
        <span> {row.original.productGroupDescription}</span>
      </div>
    ),
  },
  {
    id: "publishers",
    accessorFn: publisherNames,
    size: 130,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.publishedBy")}
      />
    ),
    cell: ({ row }) => {
      const { publisher, publishers } = row.original;

      let publisherName = "";

      if (publishers && publishers.length > 0) {
        publisherName = publishers.map((p) => p.name).join(", ");
      } else if (publisher) {
        publisherName = publisher;
      }

      return (
        <div className="max-w-[500px] flex space-x-2">
          <TruncatedText text={publisherName} />
        </div>
      );
    },
  },
  {
    id: "imprints",
    accessorFn: (product) => joinValues(product.imprints),
    size: 90,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.imprint")}
      />
    ),
    cell: ({ row }) => {
      const { imprints } = row.original;
      const imprintName = imprints?.join(", ") || "-";

      return (
        <div className="max-w-[500px] flex space-x-2">
          <TruncatedText text={imprintName} />
        </div>
      );
    },
  },
  {
    id: "publishedAt",
    accessorFn: (product) => sortableDate(product.publishedAt),
    size: 130,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.publishedOn")}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center">
          <span>{formatPublishedAt(row.original.publishedAt)}</span>
        </div>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    id: "generated",
    accessorFn: generationSortValue,
    size: options?.aPlusStatus ? 170 : 120,
    sortUndefined: "first",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t("projectDetails:table.columns.generated")}
        className="items-center"
      />
    ),
    cell: ({ row }) => {
      const aPlusStatus = options?.aPlusStatus;

      if (!aPlusStatus) {
        return <TableStatusBadges row={row} fetch />;
      }

      return (
        <div className="flex items-center justify-start gap-x-1">
          <TableStatusBadges row={row} fetch />
          <APlusStatusBadge status={aPlusStatus(row.original.id)} />
        </div>
      );
    },
  },
];
